import { CommentsApis } from 'apis/commentsAPI';
import { useGlobalConfig } from 'context/GlobalContext';
import { useState, useEffect } from 'react';
import { APIData } from 'types/cms/frontendTypes';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Textarea from '@mui/joy/Textarea';
import { Box, Chip, useTheme } from '@mui/material';
import { BglPrimaryButton } from 'components/button/BglButtons';
import { UID } from '@strapi/strapi';
import { updateRecoCommentCounts } from 'store/dataReducers/recoCommentsSlice';
import { useDispatch } from 'react-redux';

export const CommentChat = ({ contentType, contentId } : { contentType : UID.ContentType, contentId : string }) => {

  const { rc } = useGlobalConfig()
  const theme = useTheme()
  const dispatch = useDispatch()
  const [comments, setComments] = useState<any[]>([]);
  const [newCommentText, setNewCommentText] = useState('');

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const response = await CommentsApis.getComments(rc, contentType, contentId)
        
        if (response.error) {
          throw new Error(response.error);
        }

        const data = response as APIData<"plugin::comments.comment">[]
        
        setComments(data);
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    };

    fetchComments();
  }, [contentType, contentId]);

  const handlePostComment = async () => {
    // Assuming you have user information and a token for authentication
    // if (!user || !token) {
    //   alert('Please log in to post a comment.');
    //   return;
    // }

    try {
      const response = await CommentsApis.postComment(rc, contentType, contentId, newCommentText);

      if (response.error) {
        throw new Error(response.error);
      }

      const newComment = response as APIData<"plugin::comments.comment">
      setComments([newComment, ...comments]); // Optimistically update the UI
      setNewCommentText('');

      // update the comment count
      const commentCount = await CommentsApis.getCommentCount(rc, contentType, [contentId]);
      if (commentCount) {
        dispatch(updateRecoCommentCounts((commentCount as any).counts))
      }

    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };

  const loggedInUser = rc.session.getUserInfo()

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px' }}>
        <Avatar alt={loggedInUser.name?.toUpperCase()}
          sx={{ bgcolor: `${rc.utils.stringToColor(loggedInUser.name)}` }}
          src="/static/images/avatar/1.jpg" />
      <Textarea
          placeholder="Add a comment"
          minRows={3}
          value={newCommentText}
          onChange={(e) => setNewCommentText(e.target.value)}
          variant="soft"
          endDecorator={
            <Box
              sx={{
                display: 'flex',
                gap: 'var(--Textarea-paddingBlock)',
                pt: 'var(--Textarea-paddingBlock)',
                borderTop: '1px solid',
                borderColor: 'divider',
                flex: 'auto',
              }}
            >
              <BglPrimaryButton sx={{ ml: 'auto' }} 
                onClick={handlePostComment} 
                disabled={!newCommentText.trim()}
                size="small">
                  Comment
              </BglPrimaryButton>
            </Box>
        }
        sx={[
          {
            minWidth: 300,
            width: '100%',
            border: '1px solid',
            backgroundColor: theme.palette.background.default,
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
          },
          { fontStyle: 'initial' },
        ]}
      />
      
      </div>
      
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>

      {comments.length > 0 ? (

        comments.map((comment) => (

        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar alt={comment.author.name?.toUpperCase()}
              sx={{ bgcolor: `${rc.utils.stringToColor(comment.author.name)}` }}
              src="/static/images/avatar/1.jpg" />
          </ListItemAvatar>

          <ListItemText
            primary={
              <div style={{ display : 'flex', justifyContent: 'space-between'}}>
                <div>
                <strong>{comment.author.name}</strong>
                {comment.author.email === loggedInUser.email && 
                  <Chip label='You' 
                    sx={{ ml: 0.5, color: 'grey', fontSize: 12 }} 
                    size="small" variant="filled" />
                }
                </div>
                <Typography variant='bglCaption1'>{rc.dateUtils.getFormattedTime(comment.publishedAt)}</Typography>
              </div>
            }
            secondary={
              <Typography
                component="span"
                variant="body2"
                sx={{ color: 'text.primary', display: 'inline' }}>
                {comment.content}
              </Typography>
            }
            
          />
        </ListItem>
      ))
      ) : (
        <p>No comments yet</p>
      )}
      </List>

    </div>
  );
};
